import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Korean mirror of the NAATI vs court-registered translator guide. */
const TITLE = "NAATI 인증 번역과 태국 법원 등록 번역의 차이 — 무엇을 선택할까";
const DESC =
  "NAATI 인증 번역이 필요한 상황, 태국 법원·변호사협회 등록 번역이 적합한 상황, 그리고 이중 비용과 반송을 피하는 방법을 정리했습니다.";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/ko${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "NAATI 인증 번역이란 무엇입니까?",
    answer:
      "호주 NAATI가 인정한 번역자가 수행하고 서명·스탬프를 부여한 번역입니다. 호주 이민·학력·면허 관련 기관이 요구하는 대표적인 형식입니다.",
  },
  {
    question: "태국 법원 등록 번역은 어디에 사용됩니까?",
    answer:
      "태국 국내 절차, 외교부 영사국 인증, 태국 주재 대사관 제출 등 태국 기관이 인정하는 번역이 필요한 경우에 사용됩니다.",
  },
  {
    question: "둘 다 필요한 경우가 있습니까?",
    answer:
      "있습니다. 태국 측 인증 단계에서는 등록 번역이 요구되고, 호주 기관 제출용으로는 NAATI 번역이 요구되는 사안이 있습니다. 제출 기관별 요건을 먼저 확인해야 이중 비용을 피할 수 있습니다.",
  },
  {
    question: "어느 쪽이 더 빠릅니까?",
    answer:
      "단순 비교는 어렵습니다. NAATI 번역은 인증 연쇄가 짧을 수 있고, 태국 등록 번역은 영사국·대사관 확인 단계가 이어질 수 있습니다. 총 소요일은 요구되는 인증 층수에 따라 결정됩니다.",
  },
  {
    question: "NAATI 번역에도 영사국 확인이 필요합니까?",
    answer:
      "서류와 제출 기관에 따라 다릅니다. 호주 기관 제출만이면 불필요한 경우가 많지만, 태국 측 절차가 함께 있으면 별도의 인증이 필요할 수 있습니다.",
  },
  {
    question: "선택 기준을 한 줄로 정리하면?",
    answer:
      "“최종 제출 기관이 어느 번역을 서면으로 인정하는가”가 유일한 기준입니다. 번역자의 자격이 아니라 제출 기관의 요건에서 출발하십시오.",
  },
  {
    question: "반송을 피하려면 무엇을 확인해야 합니까?",
    answer:
      "이름 표기의 여권 일치, 도장·주석 문구의 완전 번역, 요구 언어, 발급 후 경과 기간, 그리고 인증 순서입니다.",
  },
];

export const Route = createFileRoute("/ko/blog/naati-vs-court-registered-translator")({
  component: KoNaatiVsCourt,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ko" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "ko") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "NAATI 번역, 태국 법원 등록 번역, 호주 이민 번역, 태국 공인 번역, 태국 서류 번역 인증",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ko"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "홈", url: "/ko" },
              { name: "가이드", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const COMPARE: Array<{ item: string; naati: string; court: string }> = [
  { item: "인정 주체", naati: "호주 NAATI 및 호주 기관", court: "태국 법원·변호사협회·외교부 영사국" },
  { item: "주요 용도", naati: "호주 비자·학력·면허 제출", court: "태국 국내 절차 및 영사 인증 연쇄" },
  { item: "표기 형식", naati: "번역자 서명·스탬프·인증 문구", court: "등록 번역자 서명 및 인증 첨부" },
  { item: "후속 인증", naati: "사안에 따라 불필요", court: "영사국 → 대사관 확인이 이어지는 경우가 많음" },
  { item: "선택 기준", naati: "제출 기관이 NAATI를 명시", court: "태국 기관 또는 주태 대사관이 요구" },
];

const STEPS: string[] = [
  "최종 제출 기관의 번역 요건을 서면으로 확인한다",
  "서류 목록과 요구 언어를 확정한다",
  "태국 측 인증이 필요한지 먼저 판단한다",
  "필요한 번역 유형(NAATI 또는 등록 번역)을 선택한다",
  "이름·날짜·번호 표기를 여권과 대조한다",
  "인증 순서에 따라 진행하고 제출 전 최종 점검한다",
];

const MISTAKES: string[] = [
  "제출 기관 요건 확인 없이 번역을 먼저 발주했다",
  "NAATI 번역만 준비했으나 태국 측 인증 단계가 남아 있었다",
  "이름 표기가 여권과 달라 동일인 확인이 되지 않았다",
  "도장·스탬프·주석을 번역에서 누락했다",
  "발급이 오래되어 제출 시점에 기간 요건을 못 맞췄다",
];

function KoNaatiVsCourt() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ko" className="text-muted-foreground hover:text-foreground">
          홈
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          가이드
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="ko" />
      <p className="answer-summary speakable-summary">
        호주 기관에 제출한다면 NAATI 인증 번역이, 태국 국내 절차나 영사 인증 연쇄가 필요하면 법원·변호사협회 등록 번역이 기준입니다.
        선택은 번역자의 자격이 아니라 최종 제출 기관이 서면으로 인정하는 형식에서 출발해야 이중 비용과 반송을 막을 수 있습니다.
      </p>

      <h2>1. 한눈에 보는 비교</h2>
      <ul>
        {COMPARE.map((c) => (
          <li key={c.item}>
            <strong>{c.item}:</strong> NAATI — {c.naati} / 등록 번역 — {c.court}
          </li>
        ))}
      </ul>

      <h2>2. 결정 순서</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 자주 나오는 실수</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 자주 묻는 질문</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 참고 링크 및 관련 서비스</h2>
      <p className="text-sm">
        참고:{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          태국 외교부 영사국
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ko/naati" className="text-primary underline">
            NAATI 인증 번역 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/sworn" className="text-primary underline">
            등록 번역자 서비스
          </Link>
        </li>
        <li>
          <Link to="/ko/lgz" className="text-primary underline">
            서류 인증·영사확인 서비스
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        내용 확인일 {PUBLISHED}. 본 가이드는 준비를 위한 일반 정보이며 개별 사안의 법률 조언이 아닙니다. 진행 전 제출 기관에서 최신 요건을 확인하십시오.
      </p>
    </article>
  );
}
